<script lang="ts">
  /**
   * The quick-capture field: one component, two hosts (STEP_5_SPEC.md D9).
   *
   * Docked at the bottom of the focus takeover and used as the page view's `C`
   * drawer. It has to behave identically in both, or the same control would mean
   * two different things depending on where it was opened from — so the
   * `@notebook` shorthand and the close-on-Enter rule live here rather than in
   * either host.
   *
   * Two rules it exists to enforce:
   *
   * * **Focus on mount.** The drawer used to open with focus still on `<body>`,
   *   and because the container's `onKeydown` only defers to a field when the
   *   *event target* is one, every following key was read as a notebook command:
   *   `j`/`k` moved the cursor, `c` closed the drawer, `Space`/`Enter` finished a
   *   task. Autofocusing is what makes `C` then type then `Enter` mean what it
   *   says.
   * * **Close on Enter.** One thought per capture. A failed font load and a
   *   browser that will not focus an input are the two ways to lose a capture;
   *   neither is worth defending with a stay-open field.
   *
   * Files via `store.appendTask`, which targets the writing page and never
   * awaits the network (§6.1) — the field clears and closes whether or not the
   * relay is reachable.
   */
  import type { NotebookStore } from '../storage/operations';

  interface Props {
    store: NotebookStore;
    /**
     * The capture text, two-way bound. The two hosts (the take-over modal's
     * docked capture, the page view's `C` drawer) keep the field local as
     * today — they pass nothing and the bindable falls back to `''`. The page
     * view's host binds it so the `beforeunload` guard can warn on unsaved text
     * the same way it warns on an in-flight task (R2.1, Task 4).
     */
    value?: string;
    /** Defaults to the Final page; always `store.appendTask` today. */
    placeholder?: string;
    /** Esc closes and Enter closes. The host owns the unmounting. */
    onClose?: () => void;
    /**
     * Step 7 (§D4): the host owns ROUTING. With a leading `@tag` the field
     * parses (`^@([^\s]+)\s*(.*)$` — tag, then the rest of the line) and
     * delegates instead of appending; the host routes a known tag to that
     * notebook's store and opens the creation modal for an unknown one. No
     * callbacks (existing tests, a host without the list) behaves exactly as
     * today: the tag is stripped and the capture appends to the given store.
     */
    onCaptureToNotebook?: (tag: string, title: string) => void;
    /** The modal path for hosts that create rather than route. */
    onCreateNotebook?: (tag: string, title: string) => void;
    /**
     * IDEAS 15: the route table as `{ tag, name }` — the same list the host
     * routes against (`routeCapture` matches `n.tag === tag`). While the field
     * holds a still-unfinished leading `@token`, a suggestion dropdown offers
     * the tags; selecting one inserts `@tag ` and the normal commit path
     * resumes. Absent or empty (old hosts): no dropdown, behaviour unchanged.
     */
    notebookTags?: ReadonlyArray<{ tag: string; name: string }>;
    /**
     * R4.6: when set, the field is EDITING that task — the host remembered what
     * landed and re-opened the field, and Enter authors `RewordTask` (the op
     * already exists in both folds and the server map; no engine change). An
     * unchanged or empty field just closes and authors nothing.
     */
    editing?: { taskId: string; title: string } | null;
    /**
     * R4.6: reports a capture that LANDED — an append or a reword — so the host
     * keeps one "last captured" memory and `e` can re-open the field as an edit.
     */
    onCommitted?: (taskId: string, title: string) => void;
  }

  let {
    value = $bindable(''),
    store,
    placeholder = 'Capture to the Final page',
    onClose,
    onCaptureToNotebook,
    onCreateNotebook,
    notebookTags = [],
    editing = null,
    onCommitted,
  }: Props = $props();

  let inputEl = $state<HTMLInputElement | null>(null);
  /** The dropdown's highlighted row (the listbox pattern, active-first). */
  let activeIndex = $state(0);

  /** R4.6: an edit relabels the field — it is not a capture. */
  const label = $derived(editing ? 'Edit last entry' : placeholder);

  $effect(() => {
    inputEl?.focus();
  });

  /**
   * R4.6: an edit request prefills the field exactly once. Keyed on the task id
   * and cleared when the request is withdrawn (the host nulls `editing` on
   * close), so re-editing the same task after a commit pre-fills again while a
   * request held open never clobbers mid-typing.
   */
  let lastEditId = $state<string | null>(null);
  $effect(() => {
    if (editing === null) {
      lastEditId = null;
      return;
    }
    if (editing.taskId === lastEditId) return;
    lastEditId = editing.taskId;
    value = editing.title;
    inputEl?.focus();
  });

  /**
   * The `@token` still being typed, or null. A dropdown candidate window: the
   * value must be a leading `@` followed by ONE whitespace-free token — once a
   * space arrives the tag is complete (the title is being typed) and the
   * dropdown closes; an `@` anywhere else is verbatim text.
   */
  const tagQuery = $derived.by(() => {
    // RAW value, not trimmed: a trailing space means the tag token is complete,
    // which is both how typing normally dismisses the dropdown and how Escape
    // does (it appends the space that ends the token).
    const match = value.match(/^@(\S*)$/);
    return match ? match[1]! : null;
  });

  /** Prefix match over the route table; the whole list while the token is bare. */
  const suggestions = $derived(
    tagQuery === null
      ? []
      : notebookTags.filter((n) => n.tag.startsWith(tagQuery.toLowerCase())),
  );
  /** Clamped, so a stale index after the list shrinks can never escape it. */
  const activeSuggestion = $derived(suggestions[Math.min(activeIndex, suggestions.length - 1)]);

  function applySuggestion(tag: string): void {
    value = `@${tag} `;
    inputEl?.focus();
  }

  function onkeydown(event: KeyboardEvent) {
    if (suggestions.length > 0) {
      // The dropdown's keys. Escape is TWO-STAGE here: dismiss the dropdown
      // first (and stop the window handler reading it as close-the-field /
      // step-back); the next Escape travels and does what it always did.
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeIndex = Math.min(activeIndex + 1, suggestions.length - 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        applySuggestion(activeSuggestion!.tag);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        // Dismissal completes the token: the same space a typist would strike
        // to move on to the title. The parse trims it, so nothing is lost.
        value = `${value} `;
        return;
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const trimmed = value.trim();

    // R4.6: an edit rewords through the existing op. A leading `@` is verbatim
    // here — the shorthand says where a NEW capture goes, and an edit is
    // already somewhere. Unchanged or empty: close, author nothing. The task is
    // captured before the await: close() nulls `editing` long before the
    // reword resolves.
    if (editing) {
      const { taskId, title } = editing;
      if (trimmed !== '' && trimmed !== title) {
        void store.rewordTask(taskId, trimmed).then(() => onCommitted?.(taskId, trimmed));
      }
      close();
      return;
    }

    if (trimmed.startsWith('@')) {
      const parsed = parseTag(trimmed);
      if (parsed && onCaptureToNotebook) {
        // The host owns the route-or-create decision (§D4) — including the
        // bare-`@tag` case, whose empty title commits a notebook, not a task.
        onCaptureToNotebook(parsed.tag, parsed.title);
        close();
        return;
      }
      if (parsed && onCreateNotebook) {
        onCreateNotebook(parsed.tag, parsed.title);
        close();
        return;
      }
    }
    const title = titleFor(value);
    if (title) void store.appendTask(title, null).then((id) => onCommitted?.(id, title));
    // Closes either way: an empty field is not worth leaving open.
    close();
  }

  /**
   * Strips the `@notebook` shorthand — or, when the host routes (§D4), parses
   * it out and delegates.
   *
   * Only a LEADING `@`: the shorthand is how you say which notebook, and an `@`
   * anywhere else is ordinary text — an email address in a captured task must
   * survive verbatim.
   */
  function titleFor(raw: string): string {
    const trimmed = raw.trim();
    return trimmed.startsWith('@') ? trimmed.replace(/^@\S*\s*/, '') : trimmed;
  }

  /** `^@([^\s]+)\s*(.*)$` on the trimmed input: tag is the first token. */
  function parseTag(raw: string): { tag: string; title: string } | null {
    const match = raw.trim().match(/^@([^\s]+)\s*(.*)$/);
    if (!match) return null;
    return { tag: match[1]!, title: match[2]! };
  }

  function close() {
    value = '';
    // R4.6: hand focus back. The drawer host unmounts on close (focus falls to
    // <body> either way); the dock's host refocuses its dialog from onClose, so
    // `e` is strikable right after a capture instead of typing into the field.
    inputEl?.blur();
    onClose?.();
  }
</script>

<label class="action quick-capture" style="cursor: text">
  <span class="quick-capture-label">{label}</span>
  <input
    bind:this={inputEl}
    bind:value
    type="text"
    data-testid="quick-capture-input"
    aria-label={label}
    placeholder="Type a task, then Enter"
    autocomplete="off"
    spellcheck="false"
    role="combobox"
    aria-expanded={suggestions.length > 0}
    aria-controls="capture-tag-suggestions"
    aria-autocomplete="list"
    aria-activedescendant={
      suggestions.length > 0 ? `capture-tag-option-${Math.min(activeIndex, suggestions.length - 1)}` : undefined
    }
    {onkeydown}
  />
  {#if suggestions.length > 0}
    <!--
      IDEAS 15: tag suggestions from the host's route table. `role="listbox"` /
      `role="option"` is the repo's own focusable-set pattern (TaskLine); the
      input stays the single focus point and `aria-activedescendant` names the
      highlighted row. Selecting authors nothing — it only inserts the tag text
      and leaves the normal commit path to run.
    -->
    <ul class="capture-tag-suggestions" id="capture-tag-suggestions" role="listbox">
      {#each suggestions as notebook, i (notebook.tag)}
        <li
          class="capture-tag-option"
          class:capture-tag-option--active={i === Math.min(activeIndex, suggestions.length - 1)}
          role="option"
          aria-selected={i === Math.min(activeIndex, suggestions.length - 1)}
          id={`capture-tag-option-${i}`}
          data-testid="capture-tag-option"
          aria-label={`@${notebook.tag} — ${notebook.name}`}
          onmousedown={(event) => {
            // Keep the input focused: the click must not blur the field.
            event.preventDefault();
            applySuggestion(notebook.tag);
          }}
        >
          <span class="capture-tag-option-tag">@{notebook.tag}</span>
          <span class="capture-tag-option-name">{notebook.name}</span>
        </li>
      {/each}
    </ul>
  {/if}
</label>

<style>
  /* One line, on the notebook's own rhythm, with no border of its own — it is a
     field drawn on the page, not a widget sitting on it. */
  /* Column layout: the label is a small muted header sitting above the input
     (upper-left), not text running beside it — beside, it reads as a second
     field and distracts from typing. `width`, not `max-width`: the field is
     an inline flex that shrink-wraps its content, so a max-width alone would
     never widen it. */
  .quick-capture {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: min(100%, 60em);
    /* The suggestion dropdown anchors to the field. */
    position: relative;
  }

  /* IDEAS 15: the suggestion list, drawn like a slip of paper under the field. */
  .capture-tag-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 5;
    list-style: none;
    margin: 2px 0 0;
    padding: 0;
    min-width: 12em;
    background: var(--paper);
    box-shadow: var(--shadow-panel);
  }

  .capture-tag-option {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 6px 12px;
    font-family: var(--font-ui);
    font-size: 13px;
    cursor: pointer;
  }

  .capture-tag-option--active {
    background: color-mix(in srgb, var(--ink) 8%, transparent);
  }

  .capture-tag-option-tag {
    font-weight: 600;
  }

  .capture-tag-option-name {
    color: var(--ink-muted);
    font-size: 12px;
  }

  .quick-capture-label {
    color: var(--ink-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  input {
    border: none;
    background: transparent;
    font: inherit;
    color: var(--ink);
    outline: none;
    /* The 16px here is the inline-size floor that stops iOS Safari zooming the
       whole viewport on focus. The font itself stays the UI face: this is a
       capture field, not a line of the notebook. */
    font-size: 16px;
    min-width: 8em;
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    .quick-capture {
      transition: none;
    }
  }
</style>